import dayjs from "dayjs";
import { formatarData } from "../collections-change/promoHelpers";

export const ESCOPO_ZONA = "zona";

export const KPIS_COMPORTAMENTAIS = [
  "conversao",
  "faturamento_por_tempo_permanencia",
];

const ehNumero = (valor) => typeof valor === "number" && Number.isFinite(valor);

const diaMes = (iso) => dayjs(String(iso).slice(0, 10)).format("DD/MM");

export const rotuloDaVigencia = (evento) =>
  `${diaMes(evento.datetime)}–${
    evento.end_datetime ? diaMes(evento.end_datetime) : "em aberto"
  }`;

const zonaDoRotulo = (evento) => {
  if (evento.escopo === "produto") return null;
  if (evento.n_zonas > 1) return `${evento.n_zonas} zonas`;
  return evento.zona_nome || null;
};

export const rotuloDoEvento = (evento) => {
  const titulo = String(evento.titulo || "").trim();
  const vigencia = rotuloDaVigencia(evento);
  return [
    titulo,
    zonaDoRotulo(evento),
    titulo.includes(vigencia) ? null : vigencia,
  ]
    .filter(Boolean)
    .join(" · ");
};

export const opcoesDeEventos = (eventos) =>
  (eventos || [])
    .filter((evento) => evento.event_id)
    .slice()
    .sort((a, b) => String(b.datetime).localeCompare(String(a.datetime)))
    .map((evento) => ({
      value: evento.event_id,
      label: rotuloDoEvento(evento),
    }));

export const intervaloInclusivo = (periodoEfetivo) =>
  periodoEfetivo?.start && periodoEfetivo?.end
    ? [
        periodoEfetivo.start,
        dayjs(periodoEfetivo.end).subtract(1, "day").format("YYYY-MM-DD"),
      ]
    : null;

export const dadosDosCards = ({ totais, kpisDaZona, escopo }) => {
  const faturamento = totais?.bruto_vendas;
  const dados = {
    faturamento_total: faturamento,
    ticket_medio: totais?.ticket_medio,
    quantidade_de_vendas: ehNumero(faturamento) ? totais?.documentos : null,
    valor_medio_faturado_item:
      ehNumero(faturamento) && totais?.unidades
        ? faturamento / totais.unidades
        : null,
  };
  if (escopo === ESCOPO_ZONA && kpisDaZona) {
    dados.conversao = kpisDaZona.conversao;
    dados.faturamento_por_tempo_permanencia =
      kpisDaZona.faturamento_por_tempo_permanencia;
  }
  return dados;
};

export const indisponiveisDoEvento = ({ dados, escopo, chaves }) =>
  chaves.filter((chave) => {
    if (escopo !== ESCOPO_ZONA && KPIS_COMPORTAMENTAIS.includes(chave)) {
      return true;
    }
    return !ehNumero(dados?.[chave]);
  });

const DESCRICOES_POR_ESCOPO = {
  zona: {
    faturamento_total:
      "Faturamento presencial das categorias vinculadas às zonas do evento, rateado igualmente entre as zonas em que cada categoria estava no dia, dentro da vigência do evento.",
    quantidade_de_vendas:
      "Vendas rateadas: cada venda conta pela parte do seu valor que caiu nas zonas do evento.",
  },
  produto: {
    faturamento_total:
      "Faturamento presencial dos SKUs do evento, sem rateio, dentro da vigência do evento.",
    quantidade_de_vendas:
      "Quantidade de vendas presenciais com ao menos um SKU do evento.",
  },
};

export const kpisDoEvento = (kpis, escopo) => {
  const descricoes =
    DESCRICOES_POR_ESCOPO[escopo === ESCOPO_ZONA ? "zona" : "produto"];
  return kpis
    .filter((kpi) => kpi.key !== "percent_vendas_sem_zona")
    .map((kpi) =>
      descricoes[kpi.key] ? { ...kpi, description: descricoes[kpi.key] } : kpi
    );
};

export const avisoDoEvento = (evento) => {
  if (!evento) return null;
  const intervalo = intervaloInclusivo(evento.periodo_efetivo);
  if (!intervalo) {
    return {
      type: "warning",
      message: `A vigência de "${evento.title}" não cruza o período selecionado.`,
      description: "Ajuste as datas para ver o faturamento do evento.",
    };
  }
  const periodo = `${formatarData(intervalo[0])} a ${formatarData(intervalo[1])}`;
  const graficos = "Os gráficos abaixo não filtram por evento.";
  if (evento.escopo === ESCOPO_ZONA) {
    return {
      type: "info",
      message: `Evento "${evento.title}" · ${periodo}`,
      description: `Faturamento rateado entre as zonas em que cada categoria estava vinculada no dia. Conversão e faturamento por atenção são das zonas do evento. ${graficos}`,
    };
  }
  return {
    type: "info",
    message: `Evento "${evento.title}" · ${periodo}`,
    description: `Faturamento direto dos SKUs do evento, sem rateio. Conversão e faturamento por atenção são medidos por zona e não existem para evento por produto. ${graficos}`,
  };
};

export const linhasDosSkus = (grupos) =>
  (grupos || [])
    .map((grupo) => ({
      produto: grupo.key,
      descricao: grupo.descricao,
      desconto_permanente_pct: grupo.desconto_permanente_pct,
      unidades: grupo.unidades,
      vendas: grupo.documentos,
      faturamento: grupo.bruto_vendas,
      ticket_medio: grupo.ticket_medio,
    }))
    .sort(
      (a, b) =>
        (b.faturamento ?? 0) - (a.faturamento ?? 0) ||
        String(a.produto).localeCompare(String(b.produto))
    );
